// utils/delayFalse.ts
import { useEffect, useRef, useState } from 'react'

/**
 * Returns a boolean that mirrors the input value, but when the value switches to false,
 * it stays true for the specified delay (in ms) before becoming false.
 *
 * @param value The boolean value to watch
 * @param delayMs The delay in milliseconds before setting to false
 */
export const useDelayFalse = (value: boolean, delayMs: number = 500): boolean => {
  const [delayed, setDelayed] = useState(value)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (value) {
      setDelayed(true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    } else {
      timeoutRef.current = setTimeout(() => {
        setDelayed(false)
        timeoutRef.current = null
      }, delayMs)
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [value, delayMs])

  return delayed
}
